/**
 * useScreenAnalytics Hook
 *
 * Consolidates analytics tracking effects for the home screen.
 * Tracks screen views, reduced motion results, and loading states.
 *
 * Extracted from index.tsx to reduce effect complexity.
 */
import { useEffect, useRef } from "react";
import { Platform } from "react-native";
import { captureEvent } from "../lib/analytics";
import { getCategoryFromScore } from "../components/modern-results";
import type { QuotaTier } from "../components/trial/QuotaExhaustedModal";

interface AnalysisData {
  id?: string | null;
  score: number | null;
  category?: string | null;
  narrator_intro?: string | null;
  summary?: string | null;
}

interface UseScreenAnalyticsOptions {
  /** Whether user prefers reduced motion (undefined while loading) */
  prefersReducedMotion: boolean | undefined;
  /** Current tier */
  currentTier: QuotaTier;
  /** Whether reduced motion results are showing */
  showReducedMotionResults: boolean;
  /** Analysis data (if available) */
  analysisData: AnalysisData | undefined;
}

/**
 * Hook that manages analytics tracking for the home screen.
 */
export function useScreenAnalytics(options: UseScreenAnalyticsOptions): void {
  const {
    prefersReducedMotion,
    currentTier,
    showReducedMotionResults,
    analysisData,
  } = options;

  const platformType = Platform.OS as "ios" | "android" | "web";
  const hasTrackedView = useRef(false);
  const lastTrackedAnalysisId = useRef<string | null>(null);

  // Track screen view (once prefersReducedMotion is resolved)
  // Wait for preference to resolve to avoid recording wrong reduced_motion value
  // eslint-disable-next-line local-rules/no-race-conditions -- single-view bootstrap telemetry gated by hasTrackedView; emits analytics only and does not coordinate shared mutable state
  useEffect(() => {
    if (hasTrackedView.current) return;
    // Don't track until prefersReducedMotion is resolved (not undefined)
    if (prefersReducedMotion === undefined) return;

    hasTrackedView.current = true;

    captureEvent("unified_analysis_view", {
      screen: "analyze-unified",
      variant: "v9_letter_zoom",
      reduced_motion: prefersReducedMotion,
      platform: platformType,
    });
  }, [prefersReducedMotion, platformType]);

  // Track successful analysis + inline results exposure for each new analysis.
  // This remains best-effort analytics only; core product flow does not depend on it.
  // eslint-disable-next-line local-rules/no-race-conditions -- one-shot per-analysis telemetry gated by lastTrackedAnalysisId; emits analytics only and does not coordinate shared mutable state
  useEffect(() => {
    if (!analysisData) return;

    const analysisId = analysisData.id ?? `${analysisData.score ?? "unknown"}`;
    if (lastTrackedAnalysisId.current === analysisId) return;
    lastTrackedAnalysisId.current = analysisId;

    captureEvent("unified_analysis_success", {
      score: analysisData.score,
      category: analysisData.category || getCategoryFromScore(analysisData.score),
      tier: currentTier,
      reduced_motion: Boolean(showReducedMotionResults),
      platform: platformType,
    });

    captureEvent("unified_results_view", {
      display_mode: showReducedMotionResults
        ? "reduced_motion_inline"
        : "interactive_inline",
      tier: currentTier,
      score: analysisData.score,
      has_narrator_intro: !!analysisData.narrator_intro,
      has_summary: !!analysisData.summary,
      platform: platformType,
    });
  }, [showReducedMotionResults, analysisData, currentTier, platformType]);
}

export default useScreenAnalytics;
